import { tWithKeyAttr as t } from "../../../i18nHelper";
import changeAvatarPopup from "./changeAvatarPopup.module.css";
import React, { useState } from "react";
import { useFormik } from "formik";
import { usePostFileMutation } from "../../../services/api/utils.api";
import { useDeleteUserAvatarMutation } from "../../../services/api/user.api";
import Modal from "../modal/Modal";
import ErrorPopup from "../errorPopup/errorPopup";
import GeneralSuccessPopup from "../generalSuccessPopup/generalSuccessPopup";

const AVATAR_UPLOAD_TYPE = 1;

interface ChangeAvatarPopupProps {
  onClose: () => void;
  onSuccess?: (newAvatarUrl?: string) => void;
  userId: number;
  userGuid: string;
  currentAvatar: string | null;
}

function ChangeAvatarPopup({
  onClose,
  onSuccess,
  userId,
  userGuid,
  currentAvatar,
}: ChangeAvatarPopupProps) {
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [imageURL, setImageURL] = useState<string | null>(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessPopup, setOpenDeleteSuccessPopup] = useState(false);

  const [deleteUserAvatar] = useDeleteUserAvatarMutation();
  const [postFile] = usePostFileMutation();

  const hasAvatar = Boolean(currentAvatar);

  const handleSubmit = async () => {
    if (image) {
      const response = await postFile({
        file: image,
        usersId: userId,
        type: AVATAR_UPLOAD_TYPE,
      });
      if ("data" in response && response.data?.urls?.[0]) {
        setOpenSuccessModal(true);
        onSuccess?.(response.data.urls[0]);
      } else {
        setOpenErrorModal(true);
      }
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImage(file);
      formik.setFieldValue("avatar", file);
      formik.setFieldValue("avatarName", file.name);
      const url = URL.createObjectURL(file);
      setImageURL(url);
    }
  };

  const formik = useFormik({
    initialValues: {
      avatar: "" as File | "",
      avatarName: "",
    },
    onSubmit: () => {
      handleSubmit();
    },
  });

  const handleDeleteAvatar = async () => {
    const confirmed = window.confirm(t("CHANGE_AVATAR_DELETE_CONFIRM"));
    if (!confirmed) return;

    try {
      const response = await deleteUserAvatar({
        guid: userGuid,
      });

      if ("data" in response) {
        formik.setFieldValue("avatar", "");
        formik.setFieldValue("avatarName", "");
        setImage(null);
        setImageURL(null);
        onSuccess?.("");
        setOpenDeleteSuccessPopup(true);
      } else {
        setOpenErrorModal(true);
      }
    } catch (error) {
      setOpenErrorModal(true);
    }
  };

  if (openErrorModal) {
    return (
      <Modal onClose={() => setOpenErrorModal(false)}>
        <ErrorPopup onClose={() => setOpenErrorModal(false)} />
      </Modal>
    );
  }

  if (openDeleteSuccessPopup) {
    return (
      <Modal onClose={() => { setOpenDeleteSuccessPopup(false); onClose(); }}>
        <GeneralSuccessPopup
          onClose={() => {
            setOpenDeleteSuccessPopup(false);
            onClose();
          }}
        />
      </Modal>
    );
  }

  if (openSuccessModal) {
    return (
      <Modal onClose={() => { setOpenSuccessModal(false); onClose(); }}>
        <GeneralSuccessPopup
          onClose={() => {
            setOpenSuccessModal(false);
            onClose();
          }}
        />
      </Modal>
    );
  }

  return (
    <article className={changeAvatarPopup.popup}>
      <div className={changeAvatarPopup.popup__content}>
        <h2 className={changeAvatarPopup.popup__title}>
          {t("CHANGE_AVATAR_TITLE")}
        </h2>
        <form
          id="updateAvatar"
          onSubmit={formik.handleSubmit}
          className={changeAvatarPopup.popup__form}
        >
          <div className={changeAvatarPopup.file_input_wrapper}>
            {formik.values.avatar && imageURL && (
              <img
                src={imageURL}
                alt="Avatar Preview"
                className={`${changeAvatarPopup.popup__img}`}
                style={{ borderRadius: "50%", objectFit: "cover" }}
              />
            )}
            <label
              htmlFor="avatar"
              className={changeAvatarPopup.popup__label}
            >
              <span>{t("CHANGE_AVATAR_UPLOAD_LABEL")}</span>
            </label>
            <input
              type="file"
              name="avatar"
              id="avatar"
              accept="image/*"
              onChange={handleImageChange}
              onBlur={formik.handleBlur}
              className={changeAvatarPopup.popup__input_hiden}
            />
          </div>

          <div className={changeAvatarPopup.popup__buttonsWrapper}>
            <button
              type="submit"
              form="updateAvatar"
              disabled={formik.isSubmitting || !image}
              className={`button hover ${changeAvatarPopup.popup__submitBtn} ${
                formik.isSubmitting || !image
                  ? changeAvatarPopup.popup__submitBtn_disabled
                  : ""
              }`}
            >
              {t("CHANGE_AVATAR_UPDATE_BUTTON")}
            </button>
            <button
              type="button"
              onClick={handleDeleteAvatar}
              disabled={!hasAvatar}
              className={`button hover ${changeAvatarPopup.popup__submitBtn} ${
                !hasAvatar ? changeAvatarPopup.popup__submitBtn_disabled : ""
              }`}
            >
              {t("CHANGE_AVATAR_DELETE_BUTTON")}
            </button>
          </div>
        </form>
      </div>
    </article>
  );
}

export default ChangeAvatarPopup;
